import { Component, computed, inject } from "@angular/core";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { forecastForStation, weatherStations } from "./weather-data";

@Component({
  selector: "app-weather-dashboard",
  standalone: true,
  imports: [RouterLink],
  templateUrl: "./weather-dashboard.component.html",
  styleUrl: "./weather-dashboard.component.scss",
})
export class WeatherDashboardComponent {
  private readonly route = inject(ActivatedRoute);
  readonly stationId = this.route.snapshot.paramMap.get("id") || "satara-pss";
  readonly currentDateTimeLabel = (() => {
    const now = new Date();
    const date = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(now);
    const time = new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(now);
    return `${date} · ${time}`;
  })();
  readonly station = computed(
    () => weatherStations[this.stationId] || weatherStations["satara-pss"],
  );
  readonly forecast = forecastForStation(this.stationId);
  readonly forecastInsights = computed(() => {
    const forecast = this.forecast;
    const wettest = forecast.reduce((current, day) =>
      day.rain > current.rain ? day : current,
    );
    const windiest = forecast.reduce((current, day) =>
      day.wind > current.wind ? day : current,
    );
    const brightest = forecast.reduce((current, day) =>
      day.irradiance > current.irradiance ? day : current,
    );
    return [
      {
        label: "Peak solar window",
        value: `${brightest.irradiance} kWh/m²`,
        note: brightest.short,
        icon: "☀",
        tone: "solar",
      },
      {
        label: "Strongest wind",
        value: `${windiest.wind} m/s`,
        note: windiest.short,
        icon: "≋",
        tone: "wind",
      },
      {
        label: "Highest rainfall",
        value: `${wettest.rain} mm`,
        note: wettest.short,
        icon: "◆",
        tone: "rain",
      },
    ];
  });
  readonly metrics = computed(() => {
    const station = this.station();
    return [
      {
        label: "Ambient Temp.",
        value: `${station.temperature}°C`,
        icon: "thermometer",
        tone: "cyan",
        note: "Live sensor",
      },
      {
        label: "Module Temp.",
        value: `${station.moduleTemperature}°C`,
        icon: "thermometer",
        tone: "yellow",
        note: `${station.moduleTemperature - station.temperature}° above ambient`,
      },
      {
        label: "POA Irradiance",
        value: `${station.irradiance} W/m²`,
        icon: "☀",
        tone: "yellow",
      },
      {
        label: "Wind Speed",
        value: `${station.wind} m/s`,
        icon: "〰",
        tone: "blue",
      },
      {
        label: "Wind Direction",
        value: station.direction,
        icon: "◉",
        tone: "slate",
      },
      {
        label: "Humidity",
        value: `${station.humidity}%`,
        icon: "♢",
        tone: "blue",
      },
      {
        label: "Rainfall",
        value: `${station.rainfall.toFixed(1)} mm`,
        icon: "♢",
        tone: "blue",
      },
      {
        label: "Cloud Cover",
        value: `${station.cloud}%`,
        icon: "☁",
        tone: "orange",
      },
      {
        label: "Air Quality",
        value: station.aqi,
        icon: "⌁",
        tone: station.aqi === "Good" ? "green" : "orange",
        wide: true,
      },
    ];
  });
}
